import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Button,
  Chip,
  Divider,
  Avatar,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Flag as FlagIcon,
  Person as PersonIcon,
  Photo as PhotoIcon,
  Comment as CommentIcon,
  AccountTree as RelationshipIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ContentFlag } from '../../services/contentModerationService';

/**
 * Props for ContentReviewCard component
 */
interface ContentReviewCardProps {
  flag: ContentFlag;
  onApprove: (flagId: string) => void;
  onReject: (flagId: string) => void;
  onViewContent?: (contentType: string, contentId: string) => void;
  loading?: boolean;
}

/**
 * ContentReviewCard Component
 * Displays a flagged content item with review actions
 *
 * Features:
 * - Flag details (reason, description, reporter)
 * - Content type indicator
 * - Approve and reject buttons
 * - View content button
 * - Timestamps
 */
const ContentReviewCard: React.FC<ContentReviewCardProps> = ({
  flag,
  onApprove,
  onReject,
  onViewContent,
  loading = false,
}) => {
  /**
   * Get content type icon
   */
  const getContentIcon = () => {
    switch (flag.contentType) {
      case 'person':
        return <PersonIcon />;
      case 'photo':
        return <PhotoIcon />;
      case 'comment':
        return <CommentIcon />;
      case 'relationship':
        return <RelationshipIcon />;
      default:
        return <FlagIcon />;
    }
  };

  /**
   * Get content type color
   */
  const getContentColor = (): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    switch (flag.contentType) {
      case 'person':
        return 'primary';
      case 'photo':
        return 'info';
      case 'comment':
        return 'warning';
      case 'relationship':
        return 'secondary';
      default:
        return 'default';
    }
  };

  /**
   * Get reason severity color
   */
  const getReasonColor = (): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    const reason = flag.reason.toLowerCase();
    if (reason.includes('offensive') || reason.includes('hate') || reason.includes('harassment')) {
      return 'error';
    }
    if (reason.includes('spam') || reason.includes('inappropriate')) {
      return 'warning';
    }
    return 'default';
  };

  /**
   * Handle approve click
   */
  const handleApprove = () => {
    onApprove(flag.id);
  };

  /**
   * Handle reject click
   */
  const handleReject = () => {
    onReject(flag.id);
  };

  /**
   * Handle view content click
   */
  const handleViewContent = () => {
    if (onViewContent) {
      onViewContent(flag.contentType, flag.contentId);
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 4,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        {/* Content Type Header */}
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Avatar
            sx={{
              bgcolor: `${getContentColor()}.main`,
              width: 48,
              height: 48,
            }}
          >
            {getContentIcon()}
          </Avatar>

          <Box flexGrow={1}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Chip
                label={flag.contentType.charAt(0).toUpperCase() + flag.contentType.slice(1)}
                size="small"
                color={getContentColor()}
              />
              <Chip
                label="Flagged"
                size="small"
                color="warning"
                icon={<FlagIcon />}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              ID: {flag.contentId.substring(0, 12)}...
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Flag Reason */}
        <Box mb={2}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Reason
          </Typography>
          <Chip
            label={flag.reason}
            color={getReasonColor()}
            size="small"
            sx={{ mb: 1 }}
          />
          {flag.description && (
            <Typography variant="body2" color="text.secondary" mt={1}>
              {flag.description}
            </Typography>
          )}
        </Box>

        {/* Reporter Information */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Reported By
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {flag.reportedByName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {flag.reportedByEmail}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Timestamp */}
        <Box display="flex" alignItems="center" gap={1}>
          <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            Flagged:
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            {flag.createdAt ? format(flag.createdAt, 'MMM d, yyyy h:mm a') : 'Unknown'}
          </Typography>
        </Box>
      </CardContent>

      {/* Action Buttons */}
      <CardActions sx={{ flexDirection: 'column', gap: 1, px: 2, pb: 2 }}>
        {onViewContent && (
          <Button
            variant="outlined"
            fullWidth
            onClick={handleViewContent}
            disabled={loading}
          >
            View Content
          </Button>
        )}
        <Box display="flex" gap={1} width="100%">
          <Button
            variant="contained"
            color="error"
            startIcon={<CheckCircleIcon />}
            onClick={handleApprove}
            disabled={loading}
            fullWidth
          >
            Remove
          </Button>
          <Button
            variant="outlined"
            color="success"
            startIcon={<CancelIcon />}
            onClick={handleReject}
            disabled={loading}
            fullWidth
          >
            Keep
          </Button>
        </Box>
      </CardActions>
    </Card>
  );
};

export default ContentReviewCard;
